import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';

import Icon, {
  CloseCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { useDispatch, useSelector } from 'react-redux';
import { Col, Row, Skeleton, Typography, Button, Input, Drawer, Menu, Badge, message } from 'antd';
import { debounce, isEmpty } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import moment from 'moment';

import Card, { FormOptionsDrawer } from './Card';

import { getFormLimit } from '../../utils/restrictions';

import { showUpselling } from '../../store/actions/paymentActions';
import MiniSearch from 'minisearch';

import './style.less';
import { deleteForm, duplicateForm } from '../../store/actions/formActions';
import { pinForm, unpinForm } from '../../store/actions/userActions';
import { deviceWidth } from '../../utils/media-query';
import { useMediaQuery } from 'react-responsive';

const FormCards = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { forms, isLoading } = useSelector((state) => state.form);
  const analytics = useSelector((state) => state.analytics);
  const { me } = useSelector((state) => state.auth);
  const { pinnedFormIds } = useSelector((state) => state.user);
  const { t } = useTranslation(['translation', 'builder']);
  const [showMissing, setShowMissing] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [optionsDrawerVisible, setOptionsDrawerVisible] = useState(null);

  const miniSearch = useMemo(
    () =>
      new MiniSearch({
        fields: ['name'], // fields to index for full-text search
        storeFields: ['name', 'id', 'link', 'createdAt', 'pinned'], // fields to return with search results
        searchOptions: {
          prefix: true,
          fuzzy: 0.2,
          combineWith: 'AND',
          processTerm: (term) => term.toLowerCase(),
        },
      }),
    [],
  );

  const displayForms = useMemo(() => {
    if (pinnedFormIds?.length > 0) {
      // show pinned forms first and then the rest
      const pinnedForms = pinnedFormIds.reduce((acc, id) => {
        const form = forms.find((form) => form.id === id);
        if (!form) return acc;
        return [...acc, { ...form, pinned: true }];
      }, []);
      const restForms = forms.filter((form) => !pinnedFormIds.includes(form.id));
      return [...pinnedForms, ...restForms];
    }
    return forms;
  }, [forms, pinnedFormIds]);

  useEffect(() => {
    miniSearch.addAll(displayForms);
    return () => miniSearch.removeAll();
  }, [displayForms, miniSearch]);

  let timeAfterSignup = 0;
  if (me && me.createdAt) timeAfterSignup = moment().diff(moment(me.createdAt), 'seconds');

  const checkFormsCount = () => {
    if (forms.length >= getFormLimit(me.plan, me.maxFormCount)) {
      dispatch(showUpselling('formcount', getFormLimit(me.plan, me.maxFormCount)));
    } else history.push('/build');
  };

  const handleMerge = async () => {
    try {
      const {
        data: { url },
      } = await axios('/auth/merge');
      window.location = url;
    } catch (e) {
      console.log(e);
    }
  };

  const handleSearch = (search) => {
    if (!search) return setSearchResults([]);
    const results = miniSearch.search(search);
    setSearchResults(results);
  };
  const debouncedSearch = useCallback(debounce(handleSearch, 100), []);

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  const isMobile = useMediaQuery({ maxWidth: deviceWidth.mobile });

  const result = useMemo(() => {
    if (searchResults.length > 0) return searchResults;
    return displayForms;
  }, [displayForms, searchResults]);

  return (
    <div className="Form-cards">
      <div className="flex Form-cards-header">
        <Typography.Title className="title">
          {`${t('hello')} ${me?.name ? me?.name.split(' ')[0] : ''}`} 👋
        </Typography.Title>
        <div className="Form-cards-header-right">
          <Input
            placeholder={t('searchForm')}
            size="large"
            type="search"
            prefix={<SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.4)', marginRight: '4px' }} />}
            value={searchInput}
            onChange={handleSearchInputChange}
          />
        </div>
      </div>
      <Row gutter={[16, 16]} className="card-container">
        {searchInput && searchInput?.length > 3 ? (
          <>
            {searchResults?.length > 0 ? (
              <>
                <Col xs={24} sm={24} md={24} lg={12} xl={8} xxl={8}>
                  <div onClick={checkFormsCount}>
                    <div className="card new">
                      <span>
                        <PlusOutlined /> {t('newForm')}
                      </span>
                    </div>
                  </div>
                </Col>
                {searchResults?.map((result) => (
                  <Col key={result.id} xs={24} sm={24} md={24} lg={12} xl={8} xxl={8}>
                    <Card
                      form={result}
                      forms={forms}
                      analytics={analytics.items}
                      user={me}
                      setOptionsDrawerVisible={setOptionsDrawerVisible}
                    />
                  </Col>
                ))}
              </>
            ) : (
              <Col xs={24}>
                <div
                  className="flex"
                  style={{
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '36px',
                  }}
                >
                  <h3>{t('home.no-forms-found')}</h3>
                  <Button onClick={() => setSearchInput('')}>{t('home.clearSearch')}</Button>
                </div>
              </Col>
            )}
          </>
        ) : (
          <>
            <Col xs={24} sm={24} md={24} lg={12} xl={8} xxl={8}>
              <div onClick={checkFormsCount}>
                <div className="card new">
                  <span>
                    <PlusOutlined /> {t('newForm')}
                  </span>
                </div>
              </div>
            </Col>

            {isLoading &&
              isEmpty(forms) &&
              [1, 2, 3, 4, 5].map((item) => (
                <Col key={item} xs={24} sm={24} md={24} lg={12} xl={8} xxl={8}>
                  <Skeleton
                    active={true}
                    title={isMobile ? false : true}
                    paragraph={isMobile ? { rows: 2, width: ['70%', '50%'] } : { rows: 1 }}
                  />
                </Col>
              ))}
            {!isEmpty(forms) &&
              displayForms.map((form, i) => (
                <Col key={i} xs={24} sm={24} md={24} lg={12} xl={8} xxl={8}>
                  <Card
                    form={form}
                    forms={forms}
                    analytics={analytics.items}
                    user={me}
                    setOptionsDrawerVisible={setOptionsDrawerVisible}
                  />
                </Col>
              ))}
            {!isLoading && isEmpty(forms) && showMissing && timeAfterSignup > 20 && (
              <Col xs={24}>
                <div>
                  <div className="message-card">
                    <h3>{t('home.missing-forms')}</h3>
                    <div>
                      {t('home.you-are-currently-logged')}{' '}
                      <b className="message-provider">{t(me.provider)}</b>.
                      {me.provider === 'whatsapp' ? (
                        <>
                          {' '}
                          {t('home.if-you-have-an-existing-g')}
                          <Button
                            type="secondary"
                            onClick={handleMerge}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginTop: '10px',
                            }}
                          >
                            <img
                              src="/google/g-logo.png"
                              alt="google-logo"
                              height="18"
                              width="18"
                            />
                            <span>{t('profile.mergeAccountButtonText')}</span>
                          </Button>
                        </>
                      ) : (
                        <span>
                          {' '}
                          {t('home.try-logging-in-using')} <b>WhatsApp</b>
                        </span>
                      )}
                    </div>
                    <Icon
                      component={CloseCircleOutlined}
                      className="close-icon"
                      onClick={() => setShowMissing(false)}
                    />
                  </div>
                </div>
              </Col>
            )}
          </>
        )}
      </Row>
      <FormOptionsDrawer
        activeForm={displayForms?.find((form) => form.id === optionsDrawerVisible)}
        optionsDrawerVisible={optionsDrawerVisible}
        setOptionsDrawerVisible={setOptionsDrawerVisible}
        analytics={analytics.items}
        forms={forms}
        user={me}
      />
    </div>
  );
};

export default FormCards;
